/**
 * Quick Product Registration Modal
 * Allows operator to register a new product during a sale
 * when a barcode is not found in the system.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Package, Save } from 'lucide-react';

interface QuickProductModalProps {
  isOpen: boolean;
  barcode: string;
  onSave: (product: QuickProductData) => void;
  onCancel: () => void;
}

export interface QuickProductData {
  barcode: string;
  name: string;
  sale_price: number;
  unit: 'un' | 'kg' | 'litro' | 'metro';
  category: string;
}

const CATEGORIES = [
  'Alimentos',
  'Bebidas',
  'Limpeza',
  'Higiene',
  'Hortifruti',
  'Padaria',
  'Açougue',
  'Frios',
  'Outros',
];

const UNITS = [
  { value: 'un', label: 'Unidade (un)' },
  { value: 'kg', label: 'Quilograma (kg)' },
  { value: 'litro', label: 'Litro (L)' },
  { value: 'metro', label: 'Metro (m)' },
] as const;

export function QuickProductModal({ isOpen, barcode, onSave, onCancel }: QuickProductModalProps) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [unit, setUnit] = useState<'un' | 'kg' | 'litro' | 'metro'>('un');
  const [category, setCategory] = useState('Outros');
  const [error, setError] = useState('');

  const handleSave = () => {
    if (!name.trim()) {
      setError('Nome do produto é obrigatório');
      return;
    }
    const priceNum = parseFloat(price);
    if (!price || isNaN(priceNum) || priceNum <= 0) {
      setError('Preço de venda deve ser maior que zero');
      return;
    }

    onSave({
      barcode,
      name: name.trim(),
      sale_price: priceNum,
      unit,
      category,
    });

    // Reset form
    setName('');
    setPrice('');
    setUnit('un');
    setCategory('Outros');
    setError('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-md rounded-xl border border-border/50 bg-card p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleKeyDown}
          >
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/20">
                  <Package className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Cadastro Rápido</h2>
                  <p className="text-sm text-muted-foreground">Produto não encontrado</p>
                </div>
              </div>
              <button
                onClick={onCancel}
                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <div className="space-y-4">
              {/* Barcode (read-only) */}
              <div>
                <label className="mb-1 block text-sm font-medium text-muted-foreground">
                  Código de Barras
                </label>
                <input
                  type="text"
                  value={barcode}
                  readOnly
                  className="w-full rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-foreground"
                />
              </div>

              {/* Name */}
              <div>
                <label className="mb-1 block text-sm font-medium text-muted-foreground">
                  Nome do Produto *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setError(''); }}
                  placeholder="Ex: Arroz Integral 5kg"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  autoFocus
                />
              </div>

              {/* Price */}
              <div>
                <label className="mb-1 block text-sm font-medium text-muted-foreground">
                  Preço de Venda (R$) *
                </label>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => { setPrice(e.target.value); setError(''); }}
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Unit */}
              <div>
                <label className="mb-1 block text-sm font-medium text-muted-foreground">
                  Unidade de Medida
                </label>
                <select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value as typeof unit)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {UNITS.map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="mb-1 block text-sm font-medium text-muted-foreground">
                  Categoria
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {/* Error */}
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
            </div>

            {/* Actions */}
            <div className="mt-6 flex gap-3">
              <button
                onClick={onCancel}
                className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                Cancelar (Esc)
              </button>
              <button
                onClick={handleSave}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 hover:shadow-lg"
              >
                <Save className="h-4 w-4" />
                Salvar e Adicionar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
